import { Injectable, Injector } from '@angular/core';
import { RestService } from './rest.service';
import { RouteService } from './route.service';
import { ApplicationStateService } from './application-state.service';
import { environment } from '../../environments/environment';
import { Entity } from '../models/entity';
import { Factory } from '../models/factories/factory';
import { Loggable } from '../models/loggable';
import { PageService } from './page.service';
import { TechnicalFeature } from '@lemberger/lemberger-am';

@Injectable({
    providedIn: 'root'
})
export class CrudService {
    constructor(private restService: RestService,
                private injector: Injector,
                private routeService: RouteService,
                private applicationStateService: ApplicationStateService) {
    }

    getAllEntitiesOfBook(className: new () => Entity, factory: Factory<Entity>): Promise<Array<Entity>> {
        return this.getAllEntities(className, factory, {
            bookId: this.applicationStateService.bookId
        });
    }

    getAllEntities(className: new () => Entity, factory: Factory<Entity>, options?: any): Promise<Array<Entity>> {
        const model = new className();

        const entities = [];

        return new Promise<Array<Entity>>((resolve, reject) => {

            const route = this.routeService.getRelativeRoute(model.getEndpointName(),
                options);

            const pageService = this.injector.get(PageService);

            return this.restService.get(route)
                .then(result => {
                    result.forEach(element => {
                        const entity = factory.buildFromObject(element);
                        if (!pageService.hasLimitedView() ||
                            (pageService.hasLimitedView() && pageService.getGetViewablePagesIDs().includes(element.pageId))
                        ) {
                            entities.push(entity);
                        }
                    });
                    resolve(entities);
                })
                .catch(err => {
                    resolve([]);
                });
        });
    }

    create(model: Entity): Promise<Entity> {

        const createRouteName = model.getEndpointName() + environment.crudEndpointsPostfixes.create;
        const route = this.routeService.getRelativeRoute(createRouteName,
            {
                bookId: this.applicationStateService.bookId
            });

        return new Promise(resolve => {
            this.restService.post(route, model)
                .then(result => {
                    resolve(result);
                });
        });
    }

    update(model: Entity): Promise<Entity> {

        const updateRouteName = model.getEndpointName() + environment.crudEndpointsPostfixes.update;
        const route = this.routeService.getRelativeRoute(updateRouteName,
            {
                bookId: this.applicationStateService.bookId,
                id: model.id
            });

        return new Promise(resolve => {
            this.restService.patch(route, model)
                .then(result => {
                    resolve(result);
                });
        });
    }

    logDirect(featureSlug: TechnicalFeature): Promise<boolean> {
        const logRoute = environment.endpointUrls.logUsage;
        const route = this.routeService.getRelativeRoute(logRoute, {});

        const payload = {
            feature_slug: featureSlug,
            feature_place: 'bookviewer',
            user_identifier_id: this.applicationStateService.userData ? this.applicationStateService.userData.userIdentifier : null,
            context_id: this.applicationStateService.bookId,
            code: this.applicationStateService.userData?.bookAccessCode?.quickLink
        };

        return this.restService.post(route, payload);
    }

    log(model: Loggable): Promise<boolean> {
        return this.logDirect(model.logType);
    }

    remove(model: Entity): Promise<boolean> {

        const deleteRouteName = model.getEndpointName() + environment.crudEndpointsPostfixes.remove;
        const route = this.routeService.getRelativeRoute(deleteRouteName,
            {
                bookId: this.applicationStateService.bookId,
                id: model.id
            });

        return new Promise(resolve => {
            this.restService.delete(route)
                .then(result => {
                    resolve(result.deleted);
                });
        });
    }
}
